import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import { useProject } from '../../contexts/ProjectContext';
import { sprintApi } from '../../api/sprint';
import { formatBuildDate, formatDuration } from '../../utils/dates';

interface Props {
  projectId: string;
  highlightSprintId?: string | null;
}

export function SprintBuildHistory({ projectId, highlightSprintId }: Props) {
  const navigate = useNavigate();
  const { buildProjectUrl } = useProject();

  const { data: recentSprints } = useQuery({
    queryKey: ['sprint', 'recent', projectId],
    queryFn: async () => {
      const [building, completed, failed] = await Promise.all([
        sprintApi.list(projectId, 'building').then(r => r.sprints),
        sprintApi.list(projectId, 'completed').then(r => r.sprints),
        sprintApi.list(projectId, 'failed').then(r => r.sprints),
      ]);
      return [...building, ...completed, ...failed]
        .sort((a, b) => new Date(b.startedAt || b.completedAt || b.failedAt || b.createdAt).getTime() -
          new Date(a.startedAt || a.completedAt || a.failedAt || a.createdAt).getTime())
        .slice(0, 8);
    },
    enabled: !!projectId,
    staleTime: 30_000,
  });

  if (!recentSprints || recentSprints.length === 0) return null;

  return (
    <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm overflow-hidden">
      <div className="px-4 py-2.5 border-b border-neutral-100">
        <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Build History</p>
      </div>
      <div className="divide-y divide-neutral-100">
        {recentSprints.map(sprint => {
          const isFailed = sprint.status === 'failed';
          const isHighlighted = sprint.id === highlightSprintId;
          return (
            <button
              key={sprint.id}
              onClick={() => navigate(buildProjectUrl('ev') + `?sprint=${sprint.id}`)}
              className={`w-full text-left flex items-start gap-3 px-4 py-2.5 hover:bg-neutral-50 transition-colors ${
                isHighlighted ? 'border-l-2 border-l-coral-500 bg-coral-50/30' :
                isFailed ? 'bg-red-50/30' : ''
              }`}
            >
              {sprint.status === 'building' ? (
                <span className="relative flex h-[15px] w-[15px] items-center justify-center shrink-0 mt-0.5">
                  <span className="animate-ping absolute inline-flex h-2.5 w-2.5 rounded-full bg-coral-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-coral-500" />
                </span>
              ) : sprint.status === 'completed' ? (
                <CheckCircle2 size={15} className="text-emerald-500 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-medium text-neutral-700 truncate">{sprint.name}</p>
                <p className="text-[10px] text-neutral-400 mt-0.5">
                  {sprint.epicIds.length} epic{sprint.epicIds.length !== 1 ? 's' : ''}
                  {sprint.execution ? ` \u00b7 ${sprint.execution.storyCount} stories` : ''}
                  {(() => { const dur = formatDuration(sprint.startedAt, sprint.completedAt || sprint.failedAt); return dur ? ` \u00b7 ${dur}` : ''; })()}
                  {' \u00b7 '}
                  {formatBuildDate(sprint.completedAt || sprint.failedAt || sprint.startedAt || sprint.createdAt)}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded uppercase ${
                  sprint.status === 'completed'
                    ? 'bg-emerald-50 text-emerald-600'
                    : sprint.status === 'building'
                    ? 'bg-coral-50 text-coral-600'
                    : 'bg-red-50 text-red-600'
                }`}>
                  {sprint.status}
                </span>
                <ArrowRight size={12} className="text-neutral-300" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
